import { Link } from 'react-router-dom';

import { buildIntegrationPrompt } from '@admin-platform/shared/integration-content';
import { endpointsForPage } from '../api-endpoints';
import { OnThisPage } from '../components/OnThisPage';
import { useDocsPage } from '../hooks/useDocsPage';

const DELTA_EXAMPLE = `const response = await fetch('https://api.revvly.ai/api/v1/usage', {
  method: 'POST',
  headers: {
    Authorization: \`Bearer \${process.env.REVVLY_TOKEN}\`,
    'Content-Type': 'application/json',
    'Idempotency-Key': usageEvent.id,
  },
  body: JSON.stringify({
    externalSubscriptionId: usageEvent.accountId,
    variableSlug: 'api-calls',
    delta: usageEvent.count,
    reportedAt: usageEvent.occurredAt,
    notes: 'Hourly usage rollup',
  }),
});

if (!response.ok) throw new Error(await response.text());
const state = await response.json();
// { currentCount, peakCount, cumulativeCount, ... }`;

const BATCH_EXAMPLE = `POST https://api.revvly.ai/api/v1/usage/batch
Authorization: Bearer <API_KEY>
Idempotency-Key: hourly-rollup-2026-08-02T14

{
  "items": [
    {
      "externalSubscriptionId": "acct_123",
      "variableSlug": "api-calls",
      "delta": 1500,
      "idempotencyKeySuffix": "acct_123"
    },
    {
      "externalSubscriptionId": "acct_456",
      "variableSlug": "api-calls",
      "delta": 900,
      "idempotencyKeySuffix": "acct_456"
    }
  ]
}

// HTTP 207: inspect results[].status independently.`;

const AGENT_PROMPT = buildIntegrationPrompt('usageReporting');

export function UsageReportingGuide() {
  useDocsPage('Report usage through REST — Revvly Docs');

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link><span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Usage reporting</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>Report metered usage with <em>REST</em>.</h1>
        <p className="dp-oneliner">
          Use direct usage mutations when your worker owns a current count, a delta, or a
          commission sale — with idempotency on every write.
        </p>
      </header>
      <OnThisPage endpoints={endpointsForPage('/docs/guides/usage-reporting')} />
      <section className="dp-section">
        <h2>Give this prompt to an integration agent</h2>
        <pre className="api-code">{AGENT_PROMPT}</pre>
      </section>
      <section className="dp-section">
        <h2>Example 1: send a usage delta</h2>
        <p className="dp-plain">
          Send positive deltas to add usage and negative deltas to remove it. Reuse the same
          idempotency key only when retrying the same logical usage event.
        </p>
        <pre className="api-code">{DELTA_EXAMPLE}</pre>
      </section>
      <section className="dp-section">
        <h2>Example 2: send an hourly batch</h2>
        <p className="dp-plain">
          Batch up to 100 deltas. The request can succeed while one item fails, so store and
          inspect each result rather than treating the 207 response as blanket success.
        </p>
        <pre className="api-code">{BATCH_EXAMPLE}</pre>
      </section>
      <section className="dp-section">
        <h2>Choose the right mutation</h2>
        <div className="api-index-list">
          <div className="api-endpoint-target" id="post-usage">
            <div className="api-endpoint-bar">
              <span className="api-method api-method-post">POST</span>
              <span className="api-url">https://api.revvly.ai/api/v1/usage</span>
            </div>
            <p className="dp-plain">Apply a positive or negative delta.</p>
          </div>
          <div className="api-endpoint-target" id="post-usage-set">
            <div className="api-endpoint-bar">
              <span className="api-method api-method-post">POST</span>
              <span className="api-url">https://api.revvly.ai/api/v1/usage/set</span>
            </div>
            <p className="dp-plain">Replace the current quantity with an absolute count.</p>
          </div>
          <div className="api-endpoint-target" id="post-usage-commission">
            <div className="api-endpoint-bar">
              <span className="api-method api-method-post">POST</span>
              <span className="api-url">https://api.revvly.ai/api/v1/usage/commission</span>
            </div>
            <p className="dp-plain">
              Record a sale amount and your transaction id for commission billing.
            </p>
          </div>
          <div className="api-endpoint-target" id="post-usage-batch">
            <div className="api-endpoint-bar">
              <span className="api-method api-method-post">POST</span>
              <span className="api-url">https://api.revvly.ai/api/v1/usage/batch</span>
            </div>
            <p className="dp-plain">
              Apply up to 100 independent deltas; receive per-item results.
            </p>
          </div>
        </div>
      </section>
      <section className="dp-section">
        <h2>Read usage back</h2>
        <p className="dp-plain">
          Reconcile against Revvly instead of re-posting after a failed run. Both reads are
          Bearer-only &mdash; no signature, no idempotency key &mdash; and are filtered to the
          key&rsquo;s scope.
        </p>
        <div className="api-index-list">
          <div className="api-endpoint-target" id="get-usage-state">
            <div className="api-endpoint-bar">
              <span className="api-method api-method-get">GET</span>
              <span className="api-url">
                https://api.revvly.ai/api/v1/usage/:subscriptionId/:variableId
              </span>
            </div>
            <p className="dp-plain">
              Current state: currentCount, peakCount, cumulativeCount, peakReachedAt. These are
              accrued unit counts, not billed amounts. currentCount is a point-in-time gauge,
              while cumulativeCount is the total for the <em>current period</em> on components
              that accumulate per period &mdash; so it resets at renewal and will not match a
              lifetime total.
            </p>
          </div>
          <div className="api-endpoint-target" id="get-usage-history">
            <div className="api-endpoint-bar">
              <span className="api-method api-method-get">GET</span>
              <span className="api-url">
                https://api.revvly.ai/api/v1/usage/:subscriptionId/:variableId/history
              </span>
            </div>
            <p className="dp-plain">
              The audit log, filtered with since / until / limit (default 100, max 1000) and a
              hasMore flag. Each entry carries the idempotency key that produced it, so a
              half-finished run can be resumed exactly.
            </p>
          </div>
        </div>
      </section>
      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/api" style={{ color: 'inherit', textDecoration: 'none' }}>
              All public API endpoints ›
            </Link>
          </li>
          <li>
            <Link to="/docs/api/events" style={{ color: 'inherit', textDecoration: 'none' }}>
              Event Ingestion API — HMAC-signed event batches ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/api/external-references"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              External references &amp; ID discovery ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
